import Controller from '@ember/controller';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import AlertsService from 'cio/services/alerts';
import SubscriptionCenterService from 'cio/services/subscription-center';
import AuthService from 'cio/services/auth';
import ApiErrorsService from 'cio/services/api-errors';
import EnvironmentService from 'cio/services/environment';
import { PURPLE_700, WHITE } from 'cio/utils/colors';
import { isEmpty } from '@ember/utils';
import { colord, extend as colordExtend } from 'colord';
import SubscriptionCenterBranding from 'cio/components/subscription-center/branding';
import { LogoSource, SubscriptionCenterNavTabs, SubscriptionCenterSettings } from 'cio/utils/subscription-center';
import AccountService from 'cio/services/account';
import RouterService from '@ember/routing/router-service';
import colordA11yPlugin from 'colord/plugins/a11y';
import isEqual from 'lodash.isequal';
import { dropTask } from 'ember-concurrency';
import FormValidations from 'cio/utils/form-validations';
import AnalyticsService from 'cio/services/analytics';

const DEFAULT_BACKGROUND_COLOR = PURPLE_700;
const DEFAULT_FOREGROUND_COLOR = WHITE;

const Validations = {
  backgroundColor(value: string) {
    if (!value.startsWith('#')) {
      return 'A hex color must be used and start with #';
    }

    if (value.length !== 7) {
      return 'A hex color must be used and be 7 characters long';
    }

    if (isEmpty(value.trim()) || !colord(value).isValid()) {
      return 'A hex color must be used';
    }

    return true;
  },

  logoPath(this: SubscriptionCenterBranding, value: string) {
    if (this.brandingValues.logoSource === LogoSource.Custom) {
      if (isEmpty(value?.trim())) {
        return 'An image must be selected for custom images';
      }

      if (!value.startsWith('http')) {
        return 'The Image URL must be a valid URL starting with http or https';
      }
    }

    return true;
  },
};

export default class SubscriptionCenterBrandingEditController extends Controller {
  @service auth!: AuthService;
  @service account!: AccountService;
  @service subscriptionCenter!: SubscriptionCenterService;
  @service alerts!: AlertsService;
  @service environment!: EnvironmentService;
  @service apiErrors!: ApiErrorsService;
  @service router!: RouterService;
  @service analytics!: AnalyticsService;

  @tracked brandingValues: SubscriptionCenterSettings = {
    logoSource: LogoSource.None,
    logoPath: null,
    backgroundColor: null,
    foregroundColor: null,
  };
  @tracked savedBrandingValues: SubscriptionCenterSettings | null = null;
  @tracked isViewingMobile = false;
  @tracked activeTab: SubscriptionCenterNavTabs = SubscriptionCenterNavTabs.GlobalUnsubscribe;

  readonly validations;
  readonly LogoSource = LogoSource;

  constructor() {
    super(...arguments);

    colordExtend([colordA11yPlugin]);
    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'brandingValues',
    });
  }

  resetDefaults() {
    this.brandingValues = {
      logoSource: LogoSource.None,
      logoPath: null,
      backgroundColor: null,
      foregroundColor: null,
    };
  }

  get showTabs() {
    return !this.subscriptionCenter.enabled && this.subscriptionCenter.topics.length > 0;
  }

  get logoSourceOptions() {
    return [
      { label: 'Custom', value: LogoSource.Custom },
      {
        label: 'Same as account settings',
        value: LogoSource.Account,
        disabled: (this.account.current.logoUrl?.length ?? 0) === 0,
      },
      { label: 'None', value: LogoSource.None },
    ];
  }

  get canEdit() {
    return this.auth.hasPermission('journeys/subscription_center/edit');
  }

  get publishDisabled() {
    return !this.canEdit || this.validations.hasErrors || !this.isDirty;
  }

  get colorPickerTriggerStyles() {
    return `background-color: ${this.brandingValues.backgroundColor}; width: 36px; height: 36px;`;
  }

  get savePreferencesButtonStyles() {
    return `background-color: ${this.brandingValues.backgroundColor}; border-color: ${this.brandingValues.backgroundColor}; color: ${this.brandingValues.foregroundColor};`;
  }

  get isDirty() {
    return !isEqual(this.brandingValues, this.savedBrandingValues);
  }

  get topics() {
    if (!this.subscriptionCenter.initialized) {
      return [];
    }

    return this.subscriptionCenter.topics;
  }

  // Color must be a hex string, with `#` included. It can come from either
  // the FlyColorPicker component (which will always be valid) or a text
  // input, so we need to do some manual verification in the latter case.
  @action
  setBackgroundColor(color: string) {
    this.validations.clearAttributeErrors('backgroundColor');

    if (!color.startsWith('#')) {
      color = `#${color}`;
    }

    this.brandingValues = {
      ...this.brandingValues,
      backgroundColor: color,
    };

    this.validations.validateAttribute('backgroundColor');
    if (Object.keys(this.validations.errors).includes('backgroundColor')) {
      return;
    }

    let parsed = colord(color);

    if (color === '') {
      this.brandingValues = {
        ...this.brandingValues,
        backgroundColor: DEFAULT_BACKGROUND_COLOR,
        foregroundColor: DEFAULT_FOREGROUND_COLOR,
      };
      return;
    }

    if (!parsed.isValid()) {
      this.brandingValues = {
        ...this.brandingValues,
        backgroundColor: color,
      };
      return;
    }

    let foregroundColor = colord(parsed.toHex() ?? '#000000').contrast('#FFFFFF') >= 3.5 ? '#FFFFFF' : '#000000';

    this.brandingValues = {
      ...this.brandingValues,
      backgroundColor: parsed.toHex(),
      foregroundColor,
    };
  }

  @action
  setLogoSource(value: LogoSource) {
    let { logoPath } = this.brandingValues;

    if (value === LogoSource.Account) {
      logoPath = this.account.current.logoUrl;
    } else {
      logoPath = null;
    }

    this.brandingValues = {
      ...this.brandingValues,
      logoPath,
      logoSource: value,
    };
    this.validations.clearAttributeErrors('logoPath');
  }

  // This is used on form Submit, but also for the @onChange of the
  // fly-styled-checkbox so we need to handle both arg types
  @action
  noop(arg: Event | boolean) {
    if (typeof arg !== 'boolean') {
      arg.preventDefault();
    }

    return;
  }

  @action
  onSelectAssetLibraryItem(path: string) {
    this.brandingValues = {
      ...this.brandingValues,
      logoPath: path,
    };
    this.validations.clearAttributeErrors('logoPath');
  }

  @action
  discardChanges() {
    if (this.savedBrandingValues == null) {
      return;
    }

    this.brandingValues = this.savedBrandingValues;
  }

  @action
  trackBackAction() {
    this.analytics.track('subscription_center', 'branding_exited');
  }

  publishBranding = dropTask(async (evt: SubmitEvent) => {
    evt.preventDefault();

    try {
      await this.validations.validateModel();
    } catch {
      // errors will be displayed in the UI where necessary. Avoid persisting
      // the changes
      return;
    }

    try {
      let newSettings = await this.environment.current.updateSubscriptionCenter(this.brandingValues);
      this.alerts.success('subscription-center.branding.save.success', 'Subscription center branding updated');

      this.savedBrandingValues = newSettings;
      this.analytics.track('subscription_center', 'branding_updated');
      this.router.transitionTo('workspace-settings.subscription_center.settings');
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  @action
  toggleViewingMobile(isViewing: boolean) {
    this.isViewingMobile = isViewing;
  }

  @action
  setActiveTab(tab: SubscriptionCenterNavTabs) {
    this.activeTab = tab;
  }
}
